import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import BusinessTable, { RowWithOptionalClick } from "../../../table";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import {
  createOrganizationClient,
  getOrganizationClients,
} from "../../../../../setup/api/services/organization";
import { IOrgClient } from "../../../../../setup/api/services/organization/models";
import { canManageApiClients } from "../../../../../setup/util/permissions";
import { AuthConfig } from "../../../../../setup/context/config/authConfig";
import { developerPortalUrl } from "../../../../../setup/constants";
import AddClientModal from "./AddClientModal";
import ClientIdLabel from "./ClientIdLabel";
import ClientCredentialsModal from "./ClientCredentialsModal";

interface ApiClientsInformationProps {
  organization: IOrganizations | undefined;
}

/**
 * BusinessTable's default export is React.memo(...)-wrapped, which erases its generic type
 * parameter — so the row type must nominally extend RowWithOptionalClick rather than relying on
 * structural compatibility alone; TS's "weak type" check otherwise rejects passing IOrgClient[]
 * directly (it shares no declared property with an all-optional type like RowWithOptionalClick).
 */
interface ClientRow extends RowWithOptionalClick, IOrgClient {}

const ApiClientsInformation = ({
  organization,
}: ApiClientsInformationProps) => {
  const { t } = useTranslation();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{
    clientId: string;
    clientSecret: string;
  } | null>(null);

  const columns = [
    { type: "clientId", value: t("lbl_client_id") },
    { type: "description", value: t("lbl_description") },
    { type: "status", value: t("lbl_status") },
    { type: "consent", value: t("lbl_auto_accept_consent") },
    { type: "otpLimit", value: t("lbl_otp_send_hourly_limit") },
    { type: "consentLimit", value: t("lbl_consent_send_hourly_limit") },
    { type: "checkoutLogin", value: t("lbl_checkout_login_mode") },
  ];

  const fetchClients = useCallback(async () => {
    if (!organization?.code) return;
    setIsLoading(true);
    try {
      const response = await getOrganizationClients(organization.code);
      setClients(response?.data ?? []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [organization?.code]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const canManageClients = canManageApiClients(authorities);

  const renderCell = (columnType: string, row: ClientRow) => {
    switch (columnType) {
      case "clientId":
        return <ClientIdLabel clientId={row.clientId} />;
      case "description":
        return (
          <span className={styles.nameValue}>{row.description || "—"}</span>
        );
      case "status":
        return (
          <span className={styles.statusContainer}>
            <span
              className={`${styles.statusBadge} ${
                row.enabled ? styles.statusActive : styles.statusInactive
              }`}>
              {row.enabled ? t("lbl_client_enabled") : t("lbl_client_disabled")}
            </span>
          </span>
        );
      case "consent":
        return (
          <span className={styles.statusContainer}>
            <span
              className={`${styles.statusBadge} ${
                row.autoAcceptConsent
                  ? styles.statusActive
                  : styles.statusInactive
              }`}>
              {row.autoAcceptConsent
                ? t("lbl_consent_automatic")
                : t("lbl_consent_manual")}
            </span>
          </span>
        );
      case "otpLimit":
        return (
          <span className={styles.nameValue}>
            {row.otpSendHourlyLimit ??
              (row.otpSendHourlyLimitDefault != null
                ? t("lbl_limit_default_with_value", {
                    value: row.otpSendHourlyLimitDefault,
                  })
                : t("lbl_otp_send_hourly_limit_default"))}
          </span>
        );
      case "consentLimit":
        return (
          <span className={styles.nameValue}>
            {row.consentSendHourlyLimit ??
              (row.consentSendHourlyLimitDefault != null
                ? t("lbl_limit_default_with_value", {
                    value: row.consentSendHourlyLimitDefault,
                  })
                : t("lbl_otp_send_hourly_limit_default"))}
          </span>
        );
      case "checkoutLogin":
        return (
          <span className={styles.nameValue}>
            {t(`lbl_checkout_login_mode_${row.checkoutLoginMode ?? "any"}`)}
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%",
        }}>
        <div className={styles.headerContainer}>
          <h3 className={styles.title}>{t("lbl_api_clients")}</h3>
          <p className={styles.subtitle}>{t("lbl_api_clients_subtitle")}</p>
          {developerPortalUrl() && (
            <a
              className={styles.devPortalLink}
              href={developerPortalUrl("/dashboard?section=api-clients")}
              target="_blank"
              rel="noopener noreferrer">
              {t("lbl_developer_portal_link")}
            </a>
          )}
        </div>
        <div className={styles.addButton}>
          {canManageClients && (
            <PrimaryButton onClick={() => setModalOpen(true)}>
              {`+ ${t("lbl_add_client")}`}
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.apiClientsTableWrapper}>
          <BusinessTable
            columns={columns}
            rows={clients}
            renderCell={(column, row) => renderCell(column, row as ClientRow)}
          />
        </div>
      </div>
      <p className={styles.helpText}>
        {t("lbl_checkout_login_mode_help")}{" "}
        {developerPortalUrl() && (
          <a
            className={styles.devPortalLink}
            href={developerPortalUrl(
              "/docs/build-a-payment/payment#checkout-login-mode"
            )}
            target="_blank"
            rel="noopener noreferrer">
            {t("lbl_checkout_login_mode_help_link")}
          </a>
        )}
      </p>

      <AddClientModal
        modalOpen={modalOpen}
        loading={creating}
        handleClose={() => setModalOpen(false)}
        handleSave={async (description) => {
          if (!organization?.code) return;
          setCreating(true);
          try {
            const result = await createOrganizationClient(organization.code, {
              description,
            });
            setCreatedCredentials(result?.data ?? null);
            setModalOpen(false);
          } catch (error) {
            console.error(error);
          } finally {
            setCreating(false);
          }
        }}
      />

      <ClientCredentialsModal
        modalOpen={!!createdCredentials}
        clientId={createdCredentials?.clientId || ""}
        clientSecret={createdCredentials?.clientSecret || ""}
        handleClose={() => {
          setCreatedCredentials(null);
          fetchClients();
        }}
      />
    </div>
  );
};

export default ApiClientsInformation;
